import { DiscountDisplay } from '@/components/promotional-offers/DiscountDisplay';
import { SortableTableHead } from '@/components/table/SortableTableHead';
import { useTableSort, type SortAccessors } from '@/hooks/useTableSort';
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@startupquickstart/frontend';
import { format } from 'date-fns';
import { Gift } from 'lucide-react';
import { useMemo } from 'react';

interface PromotionalOfferRedemption {
  id: string;
  redeemedAt: string;
  redeemedBy: 'self' | 'staff';
}

/** A promotional offer as assigned to a specific guest, with per-user state. */
export interface UserOffer {
  id: string;
  status: 'pending' | 'accepted' | 'rejected' | 'redeemed';
  expiresAt: string | null;
  redemptionCount: number;
  redeemedAt: string | null;
  createdAt: string;
  promotionalOffer: {
    id: string;
    title: string | null;
    name: string | null;
    description: string | null;
    discountType: 'percentage' | 'fixed' | null;
    discountPercentage: number | null;
    discountValue: number | null;
    maxDiscountAmount: number | null;
    redemptionType: 'self' | 'barcode';
    validFrom: string | null;
    validUntil: string | null;
    isActive: boolean | null;
    maxUserRedemptions: number | null;
    featuredImage: {
      id: string;
      url: string;
      fileLocation: string;
    } | null;
  };
  redemptions: PromotionalOfferRedemption[];
}

const statusConfig: Record<UserOffer['status'], { label: string; className: string }> = {
  pending: { label: 'Pending', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  accepted: { label: 'Accepted', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: {
    label: 'Rejected',
    className: 'bg-destructive/10 text-destructive border-destructive/20',
  },
  redeemed: { label: 'Redeemed', className: 'bg-primary/10 text-primary border-primary/20' },
};

function formatDate(dateString: string | null): string {
  if (!dateString) return 'No expiration';
  return format(new Date(dateString), 'MMM d, yyyy');
}

type OfferSortKey = 'name' | 'title' | 'status' | 'redeemed' | 'expires' | 'assigned';

/** Stable display/sort order for the per-guest assignment status. */
const STATUS_RANK: Record<UserOffer['status'], number> = {
  pending: 0,
  accepted: 1,
  redeemed: 2,
  rejected: 3,
};

const OFFER_ACCESSORS: SortAccessors<UserOffer, OfferSortKey> = {
  name: (o) => o.promotionalOffer.name?.toLowerCase() ?? null,
  title: (o) => o.promotionalOffer.title?.toLowerCase() ?? null,
  status: (o) => STATUS_RANK[o.status],
  redeemed: (o) => o.redemptionCount,
  expires: (o) => (o.expiresAt ? new Date(o.expiresAt).getTime() : null),
  assigned: (o) => new Date(o.createdAt).getTime(),
};

interface GuestOffersTableProps {
  offers: UserOffer[];
}

/**
 * Renders a guest's promotional offers in the same tabular layout as the hotel
 * Offers tab (image, name, title, discount, status) plus per-guest columns the
 * hotel list can't show (assignment status, redemptions used, per-user expiry,
 * assigned date).
 *
 * Presentational only — the parent owns data fetching, loading, and empty states.
 *
 * @param offers - The guest's assigned offers with per-user state.
 */
export function GuestOffersTable({ offers }: GuestOffersTableProps) {
  const accessors = useMemo(() => OFFER_ACCESSORS, []);
  const { sort, toggleSort, sorted } = useTableSort<UserOffer, OfferSortKey>(offers, accessors, {
    key: 'assigned',
    direction: 'desc',
  });

  return (
    <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Image</TableHead>
            <SortableTableHead
              label="Name"
              sortKey="name"
              activeKey={sort.key}
              direction={sort.direction}
              onSort={toggleSort}
            />
            <SortableTableHead
              label="Title"
              sortKey="title"
              activeKey={sort.key}
              direction={sort.direction}
              onSort={toggleSort}
            />
            <TableHead>Discount</TableHead>
            <SortableTableHead
              label="Status"
              sortKey="status"
              activeKey={sort.key}
              direction={sort.direction}
              onSort={toggleSort}
            />
            <SortableTableHead
              label="Redeemed"
              sortKey="redeemed"
              activeKey={sort.key}
              direction={sort.direction}
              onSort={toggleSort}
            />
            <SortableTableHead
              label="Expires"
              sortKey="expires"
              activeKey={sort.key}
              direction={sort.direction}
              onSort={toggleSort}
            />
            <SortableTableHead
              label="Assigned"
              sortKey="assigned"
              activeKey={sort.key}
              direction={sort.direction}
              onSort={toggleSort}
            />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((userOffer) => {
            const offer = userOffer.promotionalOffer;
            const status = statusConfig[userOffer.status] ?? statusConfig.pending;
            return (
              <TableRow key={userOffer.id}>
                <TableCell>
                  <div className="flex h-10 w-14 items-center justify-center overflow-hidden rounded-md bg-muted">
                    {offer.featuredImage ? (
                      <img
                        src={offer.featuredImage.url}
                        alt={offer.title || offer.name || 'Offer'}
                        className="h-10 w-14 object-cover"
                      />
                    ) : (
                      <Gift className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </TableCell>
                <TableCell className="font-medium text-zinc-900">
                  {offer.name || '—'}
                </TableCell>
                <TableCell className="text-muted-foreground">{offer.title || '—'}</TableCell>
                <TableCell>
                  <DiscountDisplay
                    discountType={offer.discountType === 'fixed' ? 'fixed_amount' : 'percentage'}
                    discountPercentage={offer.discountPercentage ?? undefined}
                    discountValue={offer.discountValue ?? undefined}
                    maxDiscountAmount={offer.maxDiscountAmount ?? undefined}
                  />
                </TableCell>
                <TableCell>
                  <Badge className={status.className}>{status.label}</Badge>
                </TableCell>
                <TableCell>
                  {offer.maxUserRedemptions
                    ? `${userOffer.redemptionCount} / ${offer.maxUserRedemptions}`
                    : userOffer.redemptionCount}
                </TableCell>
                <TableCell>{formatDate(userOffer.expiresAt)}</TableCell>
                <TableCell>{formatDate(userOffer.createdAt)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
